/**
 * Layer 0-1 moderation: structural and lexical checks.
 *
 * Runs in the browser for instant feedback AND server-side as the actual gate;
 * the client copy is a convenience and is never trusted. Everything here is
 * pure, synchronous and free, so it runs on every keystroke-debounce in the
 * builder. The paid ML pass (Bedrock Guardrails) only sees submissions that
 * survive this.
 */
import {
  ALL_BRANDS,
  BRAND_DISPLAY,
  KEYWORDS,
  MIN_CONTAINMENT_LENGTH,
  STANDALONE,
  STRICT,
} from './brands.js'
import { damerauLevenshtein, thresholdFor } from './distance.js'
import { squashedSkeleton, tokens } from './normalize.js'
import { hasProfanity, profanityMatches } from './profanity.js'

export type ModerationCode =
  | 'profanity'
  | 'brand-impersonation'
  | 'phishing-pattern'
  | 'high-risk-term'

export type ModeratedField = 'name' | 'tagline' | 'slug'

export interface Finding {
  readonly code: ModerationCode
  readonly field: ModeratedField
  /** What matched. For logs and the review queue -- never shown to the user. */
  readonly evidence: string
}

export interface Submission {
  readonly name: string
  readonly tagline: string
  readonly slug: string
}

/**
 * User-facing copy.
 *
 * Deliberately non-specific. Naming the exact rule that fired turns the error
 * message into an oracle an attacker can iterate against.
 */
export const MODERATION_MESSAGES: Readonly<Record<ModerationCode, string>> = {
  profanity: "That wording won't work here. Try something else.",
  'brand-impersonation':
    'That looks like it could be confused with an existing company. Pick something distinct to your startup.',
  'phishing-pattern':
    'That combination of words is one we cannot publish. Pick something distinct to your startup.',
  'high-risk-term':
    'That combination of words is one we cannot publish. Pick something distinct to your startup.',
}

function brandFindings(field: ModeratedField, text: string): Finding[] {
  const found: Finding[] = []
  const squashed = squashedSkeleton(text)
  if (squashed.length === 0) return found

  const wordTokens = tokens(text)
  const display = (brand: string) => BRAND_DISPLAY.get(brand) ?? brand

  for (const brand of STRICT) {
    // Containment: `paypalofficial`, `myfedexportal`.
    const contained = brand.length >= MIN_CONTAINMENT_LENGTH && squashed.includes(brand)

    // Token-exact: `paypal-official` -> ['paypal', 'official'].
    const asToken = wordTokens.includes(brand)

    // Fuzzy: `nkie` -> `nike`. Whole-string and per-token, since a typo-squat
    // may be the entire slug or just one word of a name.
    const threshold = thresholdFor(brand.length)
    const fuzzy =
      threshold > 0 &&
      (damerauLevenshtein(squashed, brand) <= threshold ||
        wordTokens.some((t) => damerauLevenshtein(t, brand) <= threshold))

    if (contained || asToken || fuzzy) {
      found.push({ code: 'brand-impersonation', field, evidence: display(brand) })
    }
  }
  return found
}

function phishingFindings(field: ModeratedField, text: string): Finding[] {
  const found: Finding[] = []
  const squashed = squashedSkeleton(text)
  if (squashed.length === 0) return found

  for (const term of STANDALONE) {
    if (squashed.includes(term)) {
      found.push({ code: 'high-risk-term', field, evidence: term })
    }
  }

  // A brand alone is not enough for the contextual tier, and a keyword alone
  // is never enough -- `acme-support` is a perfectly good name. The pairing is
  // the signal.
  //
  // Crucially the two must be ADJACENT in the squashed form, not merely both
  // present. Requiring only co-occurrence blocks real names: "Startup Support"
  // contains `ups`, and "Cryptography Support" contains `crypto`. Adjacency
  // keeps `paypal-verify`, `cryptoverify` and `verify-coinbase` while letting
  // both of those through, because separators are already stripped by the time
  // we compare.
  outer: for (const brand of ALL_BRANDS) {
    for (const keyword of KEYWORDS) {
      if (squashed.includes(brand + keyword) || squashed.includes(keyword + brand)) {
        found.push({
          code: 'phishing-pattern',
          field,
          evidence: `${BRAND_DISPLAY.get(brand) ?? brand}+${keyword}`,
        })
        break outer
      }
    }
  }
  return found
}

/** Screen one field. Exported for targeted tests and per-field UI feedback. */
export function screenField(field: ModeratedField, text: string): readonly Finding[] {
  if (text.trim().length === 0) return []
  const found: Finding[] = []

  if (hasProfanity(text)) {
    for (const word of profanityMatches(text)) {
      found.push({ code: 'profanity', field, evidence: word })
    }
    // hasProfanity can match without a resolvable original word; still block.
    if (found.length === 0) found.push({ code: 'profanity', field, evidence: '(obfuscated)' })
  }

  found.push(...brandFindings(field, text))
  found.push(...phishingFindings(field, text))
  return found
}

/** Screen a whole submission. */
export function screen(input: Submission): readonly Finding[] {
  return [
    ...screenField('name', input.name),
    ...screenField('tagline', input.tagline),
    ...screenField('slug', input.slug),
  ]
}

export function isBlocked(findings: readonly Finding[]): boolean {
  return findings.length > 0
}

/** The single message to show, when several rules fire at once. */
export function userMessage(findings: readonly Finding[]): string | undefined {
  const first = findings[0]
  return first ? MODERATION_MESSAGES[first.code] : undefined
}

export { CONTEXTUAL, STRICT } from './brands.js'
export { skeleton, squashedSkeleton, tokenSkeleton, tokens } from './normalize.js'
export { damerauLevenshtein } from './distance.js'
